import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Button,
} from '@mui/material';
import {
  Add,
  Search,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card, ClubWithCounts } from '../../types';
import { useTranslation } from 'react-i18next';
import CardsTable from '../../components/cards/CardsTable';

const VisitorCardsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [cards, setCards] = useState<Card[]>([]);
  const [visitorClubs, setVisitorClubs] = useState<ClubWithCounts[]>([]);
  const [selectedClubId, setSelectedClubId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchVisitorClubs();
  }, []);

  useEffect(() => {
    if (selectedClubId) {
      fetchVisitorCards();
    } else {
      // Clear cards when no club is selected
      setCards([]);
    }
  }, [selectedClubId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVisitorClubs = async () => {
    try {
      setLoading(true);
      // Fetch all visitor clubs - club managers can see all visitor clubs
      const response = await api.get<ClubWithCounts[]>('/visitor-clubs');
      setVisitorClubs(response.data);
      
      // Don't auto-select - leave it empty by default
      setSelectedClubId('');
    } catch (error) {
      console.error('Failed to fetch visitor clubs:', error);
      // Fallback to filtering from clubs-with-counts
      try {
        const response = await api.get<ClubWithCounts[]>('/clubs-with-counts');
        const visitorClubsList = response.data.filter(club => club.is_visitor_club === true);
        setVisitorClubs(visitorClubsList);
        setSelectedClubId('');
      } catch (fallbackError) {
        console.error('Failed to fetch clubs:', fallbackError);
        setVisitorClubs([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitorCards = async () => {
    if (!selectedClubId) return;
    
    try {
      setLoading(true);
      const response = await api.get<Card[]>(`/cards/club/${selectedClubId}`);
      setCards(response.data);
    } catch (error) {
      console.error('Failed to fetch visitor cards:', error);
    } finally {
      setLoading(false);
    }
  };


  const filteredCards = cards.filter(card => {
    const nome = card.nome || '';
    const cognome = card.cognome || '';
    const email = card.email || '';
    
    return nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
           cognome.toLowerCase().includes(searchTerm.toLowerCase()) ||
           email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (visitorClubs.length === 0 && !loading) {
    return (
      <Container maxWidth="lg">
        <Alert severity="info" sx={{ mt: 4 }}>
          {t('visitors.noVisitorClubs')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>{t('visitors.title')}</Typography>
        
        {/* Club Selector */}
        {visitorClubs.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Select Visitor Club</InputLabel>
              <Select
                value={selectedClubId}
                onChange={(e) => setSelectedClubId(e.target.value)}
                label="Select Visitor Club"
              >
                {visitorClubs.map((club) => (
                  <MenuItem key={club.record_id} value={club.record_id}>
                    {club.club_name} ({club.total_cards} cards)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedClubId && (
              <Chip 
                label={`Active: ${visitorClubs.find(c => c.record_id === selectedClubId)?.active_cards || 0}`}
                color="success"
              />
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              size="small"
              placeholder={t('visitors.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
          </Box>
          
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`/app/visitors/release?clubId=${selectedClubId}`)}
            disabled={!selectedClubId}
            sx={{
              background: 'linear-gradient(45deg, #ff00fb 30%, #ff66fc 90%)',
              boxShadow: '0 3px 5px 2px rgba(255, 0, 251, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #cc00c8 30%, #ff00fb 90%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 10px 4px rgba(255, 0, 251, .3)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            {t('visitors.createVisitorCard')}
          </Button>
        </Box>
      </Box>

      {!selectedClubId ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Select a Visitor Club
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please select a visitor club from the dropdown above to view its cards.
          </Typography>
        </Paper>
      ) : filteredCards.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('visitors.noCardsInClub')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? t('visitors.noCardsInClub') : t('visitors.noCardsInClub')}
          </Typography>
        </Paper>
      ) : (
        <CardsTable 
          cards={filteredCards} 
          onRefresh={fetchVisitorCards}
          showClubColumn={false}
        />
      )}
    </Container>
  );
};

export default VisitorCardsPage;